import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import FairPriceView from '@/components/polymer/FairPriceView';
import InventoryChangeView from '@/components/polymer/InventoryChangeView';
import RatesView from '@/components/polymer/RatesView';
import MarketsView from '@/components/polymer/MarketsView';
import UsitcChart from '@/components/polymer/UsitcChart';
import FeedstocksTab from '@/components/polymer/FeedstocksTab';
import DemandProductionCapacityChart from '@/components/charts/DemandProductionCapacityChart';
import PolymerDashboardLayout from '@/components/polymer/PolymerDashboardLayout';
import PolymerTotalsView from '@/components/polymer/PolymerTotalsView';
import FreeTrialFade from '@/components/common/FreeTrialFade';
import type { PolymerDashboardConfig } from '@/types/polymer-dashboard.types';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { getCachedACCData, getPPInventoryMetrics } from '@/services/marketIntelService';
import { useTheme } from '@/contexts/ThemeContext';

const DEFAULT_DATE_FROM = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return d.toISOString().split('T')[0];
})();

const DEFAULT_DATE_TO = new Date().toISOString().split('T')[0];

const PP_CONFIG: PolymerDashboardConfig = {
  polymer: 'PP',
  polymerFullName: 'Polypropylene',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview' },
    { key: 'fair-price', label: 'Fair Resin Price' },
    { key: 'totals', label: 'Totals' },
    { key: 'inventory-change', label: 'Inventory Change' },
    { key: 'demand-production', label: 'Demand/Production/Capacity' },
    { key: 'feedstocks', label: 'Feedstocks' },
    { key: 'usitc', label: 'USITC' },
    { key: 'rates', label: 'Rates' },
    { key: 'markets', label: 'Markets' },
  ],
};

const ChartPanel: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => (
  <div className={`bg-rs-off-black/60 backdrop-blur-sm border border-rs-jet-light/50 rounded-xl shadow-lg shadow-black/20 p-5 ${className || ''}`}>
    <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 whitespace-nowrap">
      {title}
    </h3>

    {children}
  </div>
);

const PPDashboard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isDark } = useTheme();
  const [accData, setAccData] = useState<any[]>([]);
  const [inventoryMetrics, setInventoryMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);
  const [isSmallDpcSelector, setIsSmallDpcSelector] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [hideChartLabels, setHideChartLabels] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1299px)').matches,
  );
  const [isSmallOverview, setIsSmallOverview] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const chartLabelsMq = window.matchMedia('(max-width: 1299px)');
    const overviewMq = window.matchMedia('(max-width: 750px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallDpcSelector(e.matches);
    const chartLabelsHandler = (e: MediaQueryListEvent) => setHideChartLabels(e.matches);
    const overviewHandler = (e: MediaQueryListEvent) => setIsSmallOverview(e.matches);
    mq.addEventListener('change', handler);
    chartLabelsMq.addEventListener('change', chartLabelsHandler);
    overviewMq.addEventListener('change', overviewHandler);
    return () => {
      mq.removeEventListener('change', handler);
      chartLabelsMq.removeEventListener('change', chartLabelsHandler);
      overviewMq.removeEventListener('change', overviewHandler);
    };
  }, []);

  useEffect(() => {
    if (hasFetched.current || !isAuthenticated) return;
    hasFetched.current = true;
    setLoading(true);
    Promise.all([
      getCachedACCData('pp')
        .then((data) => setAccData(data))
        .catch(() => setAccData([])),
      getPPInventoryMetrics()
        .then((data) => setInventoryMetrics(data))
        .catch(() => setInventoryMetrics([])),
    ]).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const monthlyData = keepFirstEntryPerMonth(
    [...inventoryMetrics].sort((a, b) =>
      (a.date || a.record_date) < (b.date || b.record_date) ? -1 : 1
    )
  );

  const monthlyLabels = monthlyData.map((d) => {
    try {
      return format(
        new Date((d.date || d.record_date) + 'T00:00:00'),
        'MMM yyyy'
      );
    } catch {
      return d.date;
    }
  });

  const renderTabContent = (activeTab: string) => {
    if (activeTab === 'overview') {
      return (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {isSmallOverview ? (
              <FairPriceView
                polymer="PP"
                dateFrom={DEFAULT_DATE_FROM}
                dateTo={DEFAULT_DATE_TO}
                labelMode="overview"
                fullBleed
              />
            ) : (
              <ChartPanel title="Fair Resin Price" className="flex flex-col">
                <FairPriceView
                  polymer="PP"
                  dateFrom={DEFAULT_DATE_FROM}
                  dateTo={DEFAULT_DATE_TO}
                  labelMode="overview"
                />
              </ChartPanel>
            )}

            {isSmallOverview ? (
              <div className="-mx-6">
                <h3 className="text-sm font-medium text-rs-off-white/70 uppercase tracking-wider mb-4 text-center px-4">
                  Demand, Production & Capacity
                </h3>
                <div className="h-[400px] px-2">
                  <DemandProductionCapacityChart
                    production={monthlyData.map((d) => d.domestic_production ?? null)}
                    demand={monthlyData.map((d) => d.domestic_demand ?? null)}
                    capacity={monthlyData.map((d) => d.capacity ?? null)}
                    labels={monthlyLabels}
                    polymer="PP"
                    showDataLabels={false}
                    isOverviewPage={true}
                  />
                </div>
              </div>
            ) : (
              <ChartPanel title="Demand, Production & Capacity" className="flex flex-col">
                <div className="flex-1 flex flex-col min-h-0">
                  <DemandProductionCapacityChart
                    production={monthlyData.map((d) => d.domestic_production ?? null)}
                    demand={monthlyData.map((d) => d.domestic_demand ?? null)}
                    capacity={monthlyData.map((d) => d.capacity ?? null)}
                    labels={monthlyLabels}
                    polymer="PP"
                    showDataLabels={false}
                    showBorder
                    isOverviewPage={true}
                  />
                </div>
              </ChartPanel>
            )}
          </div>

          {isSmallOverview ? (
            <FeedstocksTab polymer="PP" fullBleed />
          ) : (
            <ChartPanel title="Feedstocks">
              <FeedstocksTab polymer="PP" />
            </ChartPanel>
          )}
        </div>
      );
    }

    switch (activeTab) {
      case 'totals':
        return (
          <PolymerTotalsView
            data={inventoryMetrics}
            polymer="PP"
            fullBleed
          />
        );

      case 'fair-price':
        return (
          <FairPriceView
            polymer="PP"
            dateFrom={DEFAULT_DATE_FROM}
            dateTo={DEFAULT_DATE_TO}
            labelMode="tab"
            fullBleed
          />
        );

      case 'inventory-change':
        return (
          <InventoryChangeView
            data={inventoryMetrics}
            inventoryChangeKey="inventory_change_monthly"
            inventoryChangeCumulativeKey="inventory_change_cumulative"
            polymer="PP"
            fullBleed
          />
        );

      case 'demand-production':
        return (
          <div className={`flex flex-col gap-6 ${isSmallDpcSelector ? 'p-0 -mx-6' : 'p-4'}`}>
            <div className={`border border-rs-jet-light rounded-lg ${isSmallDpcSelector ? 'p-2' : 'p-4'} ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
              <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isSmallDpcSelector ? 'px-2 text-center' : ''}`}>
                Demand, Production & Capacity
              </h3>

              <div className="h-[700px]">
                <DemandProductionCapacityChart
                  production={monthlyData.map((d) => d.domestic_production ?? null)}
                  demand={monthlyData.map((d) => d.domestic_demand ?? null)}
                  capacity={monthlyData.map((d) => d.capacity ?? null)}
                  labels={monthlyLabels}
                  polymer="PP"
                  showDataLabels={!hideChartLabels}
                />
              </div>
            </div>
          </div>
        );

      case 'feedstocks':
        return <FeedstocksTab polymer="PP" fullBleed />;

      case 'usitc':
        return (
          <UsitcChart
            polymer="PP"
            dateFrom={DEFAULT_DATE_FROM}
            dateTo={DEFAULT_DATE_TO}
          />
        );

      case 'rates':
        return <RatesView polymer="PP" />;

      case 'markets':
        return (
          <MarketsView
            polymer="PP"
            data={accData}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rs-black">
        <Loader2 className="animate-spin opacity-30 w-16 h-16 text-rs-green" />
      </div>
    );
  }

  const renderContent = (activeTab: string) => {
    const blurredTabs = ['totals', 'inventory-change', 'demand-production', 'rates', 'markets'];
    return (
      <FreeTrialFade enabled={blurredTabs.includes(activeTab)}>
        {renderTabContent(activeTab)}
      </FreeTrialFade>
    );
  };

  return (
    <PolymerDashboardLayout
      config={PP_CONFIG}
      renderContent={renderContent}
    />
  );
};

export default PPDashboard;